import { useEffect, useState } from 'react'
import { HttpVerb } from '../../../../../../types/api-types'
import { FieldTypes, OrganisationCouncilFieldsEdit, OrganisationFieldsEdit } from '../../../../../../types/form-types'
import { FormDelegate } from '../../../../../../types/routing-types'
import ConfirmTransition from '../../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../../components/errors/messages'
import AddressField from '../../../../../components/wells/address-field'
import LookupField from '../../../../../components/wells/lookup-field'
import LookupSearch from '../../../../../components/wells/lookup-search'
import WellEditButtons from '../../../../../components/wells/well-edit-buttons'
import { useGetWellData, useSaveWellData } from '../../../../../hooks/use-api'
import useEditForm from '../../../../../hooks/use-edit-form'
import useToastMessage from '../../../../../hooks/use-toast-message'
import useUser from '../../../../../hooks/use-user'
import { getLookupData } from '../../../../../services/api'
import { isSystemAdmin } from '../../../../../services/user-utils'
import OrganisationCouncilFields from '../../organisation-council-fields'
import { useParamsOr404 } from '../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  CheckableField,
  CheckableFields,
  Divider,
  FormField,
  Heading,
  InlineNotification,
  RecordContainer,
  RecordContent,
  RecordFooter,
  RepeatableSection,
  TextInput,
  Toggle,
  TypeaheadOption,
  useTitleChange,
  WorkflowNav,
} from '@msaf/core-react'

function Organisation({ setIsDirty }: FormDelegate) {
  useTitleChange('Administration | Edit organisation')
  const navigate = useNavigate()
  const { user } = useUser()
  const [isNewCompany, setIsNewCompany] = useState(false)
  const [isNewUser, setIsNewUser] = useState(false)
  const { id: organisationId } = useParamsOr404('id')
  const getAndUpdateUrl = `administration/organisation/${organisationId}/edit`
  const saveUrl = organisationId === 'new' ? `administration/organisation` : getAndUpdateUrl
  const cancelRoute = organisationId === 'new' ? `/admin/organisations` : `/admin/organisations/${organisationId}/view`

  const stateQueryResult = useGetWellData(getAndUpdateUrl)
  const saveMutationResult = useSaveWellData(saveUrl, organisationId === 'new' ? HttpVerb.POST : HttpVerb.PUT)

  const { fields, isLoading, error, isSaveError, setValue, handleSave, validationErrors, isSaved } =
    useEditForm<OrganisationFieldsEdit>({
      stateQueryResult,
      saveMutationResult,
      setIsDirty,
    })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: organisationId === 'new' ? 'Organisation created.' : 'Organisation details updated.',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  useEffect(() => {
    // Reset the `isNewCompany` flag
    if (!fields.company && isNewCompany) {
      setIsNewCompany(false)
    }
    if (!fields.email && isNewUser) {
      setIsNewUser(false)
    }
  }, [fields.company, fields.email, isNewUser, isNewCompany])

  return (
    <RecordContainer
      workflowNav={
        <WorkflowNav
          sections={[{ links: [{ path: `/admin/organisations/${organisationId}/edit`, label: 'Details' }] }]}
        />
      }
    >
      <RecordContent>
        <ButtonsContainer containerStyle='left'>
          <Button
            onClick={() => navigate(`/admin/organisations`, { state: { activeTab: 2 } })}
            label='Back'
            iconAlignment='left'
            icon='arrow-left'
            buttonStyle='text-action'
          />
        </ButtonsContainer>
        <Heading level={2}>Organisation details</Heading>

        <FormField label='Organisation' labelId='company' validationMessages={validationErrors?.company} isRequired>
          <LookupSearch
            selectType='async-creatable'
            isSkeleton={isLoading}
            searchFunction={(companyName: string) =>
              getLookupData('organisation', user?.token ?? '', user?.auth_method, { companyName, isExternal: true })
            }
            fieldId='organisation'
            labelledBy='company'
            value={`${fields.company ?? ''}`}
            setValue={(option, isNew) => {
              setValue('company', option.value)
              setIsNewCompany(!!isNew)
            }}
            fieldFilters={{ isExternal: true }}
            isDisabled={organisationId !== 'new'}
          />
        </FormField>
        {fields.company && !isNewCompany && organisationId === 'new' && (
          <div className='c-input-container--default'>
            <InlineNotification isDismissable={false} messageType='warning'>
              This organisation already exists, to be added as a new organisation, the name will need to be updated
            </InlineNotification>
          </div>
        )}
        <FormField
          isSkeleton={isLoading}
          label='Type'
          labelId='companyType'
          validationMessages={validationErrors?.companyType}
          isRequired
        >
          <LookupField
            isSkeleton={isLoading}
            fieldId='companyType'
            labelledBy='companyType'
            value={fields.companyType ?? ''}
            setValue={({ value }) => setValue('companyType', value)}
          />
        </FormField>
        <AddressField
          isSkeleton={isLoading}
          addressFields={{
            addressLine1: `${fields.addressLine1 ?? ''}`,
            addressLine2: `${fields.addressLine2 ?? ''}`,
            addressLine3: `${fields.addressLine3 ?? ''}`,
            addressLine4: `${fields.addressLine4 ?? ''}`,
            addressLine5: `${fields.addressLine5 ?? ''}`,
            addressPostcode: `${fields.addressPostcode ?? ''}`,
            isManualEntry: false,
          }}
          labelledBy='companyAddress'
          validationFieldErrors={validationErrors ?? []}
          allowManualEntry={false}
          setAddressFields={(addressFields) => {
            setValue('addressLine1', addressFields.addressLine1 ?? '')
            setValue('addressLine2', addressFields.addressLine2 ?? '')
            setValue('addressLine3', addressFields.addressLine3 ?? '')
            setValue('addressLine4', addressFields.addressLine4 ?? '')
            setValue('addressLine5', addressFields.addressLine5 ?? '')
            setValue('addressPostcode', addressFields.addressPostcode ?? '')
          }}
          isRequired
        />
        <FormField isSkeleton={isLoading} label='Contact number' htmlFor='companyNumber'>
          <TextInput
            isSkeleton={isLoading}
            id='companyNumber'
            value={fields.companyNumber ?? ''}
            setValue={(value: string) => setValue('companyNumber', value)}
          />
        </FormField>

        <Divider />
        <Heading level={2}>Organisation permissions</Heading>
        {organisationId !== 'new' && (
          <>
            <FormField isSkeleton={isLoading} label='' htmlFor='status'>
              <Toggle
                id='status'
                isChecked={!fields.isDeleted}
                isSkeleton={isLoading}
                onChange={() => setValue('isDeleted', !fields.isDeleted)}
                name='status'
              >
                {fields.isDeleted ? 'Archived' : 'This is a currently active organisation'}
              </Toggle>
            </FormField>
            <RepeatableSection disableNumberedSections heading='' sectionName='' isSkeleton={isLoading}>
              {fields.councils?.map((council: OrganisationCouncilFieldsEdit, index) => (
                <OrganisationCouncilFields
                  key={council.id}
                  fields={council}
                  isLoading={isLoading}
                  setValue={(key: keyof OrganisationCouncilFieldsEdit, val: FieldTypes) =>
                    setValue('councils', val, key, index)
                  }
                  validationErrors={validationErrors ?? {}}
                  company={fields.company ?? ''}
                  isDisabledSection={!isSystemAdmin(user) && user?.council !== council.companyCouncil}
                />
              ))}
            </RepeatableSection>
          </>
        )}
        {organisationId === 'new' && (
          <>
            <FormField
              isSkeleton={isLoading}
              label='Regional council'
              labelId='companyCouncil'
              validationMessages={validationErrors?.companyCouncil}
              isRequired
            >
              <LookupField
                isSkeleton={isLoading}
                fieldId='organisation'
                labelledBy='companyCouncil'
                value={fields.companyCouncil ?? ''}
                setValue={({ value }) => setValue('companyCouncil', value)}
                isDisabled={organisationId !== 'new'}
              />
            </FormField>
            <FormField
              isSkeleton={isLoading}
              label='Can create new well records'
              htmlFor='canCreateWellLabel'
              validationMessages={validationErrors?.canCreateWell}
              isRequired
            >
              <CheckableFields ariaLabelledBy='canCreateWellLabel' isHorizontal>
                <CheckableField
                  id='canCreateWellYes'
                  name='canCreateWell'
                  describedBy='canCreateWellLabel'
                  label='Yes'
                  isSkeleton={isLoading}
                  type='radio'
                  checked={!!fields.canCreateWell}
                  onChange={() => setValue('canCreateWell', true)}
                />
                <CheckableField
                  id='canCreateWellNo'
                  name='canCreateWell'
                  describedBy='canCreateWellLabel'
                  label='No'
                  isSkeleton={isLoading}
                  type='radio'
                  checked={!fields.canCreateWell}
                  onChange={() => setValue('canCreateWell', false)}
                />
              </CheckableFields>
            </FormField>
            <FormField
              isSkeleton={isLoading}
              label='Can edit assigned well records'
              htmlFor='canEditWellLabel'
              validationMessages={validationErrors?.canEditWell}
              isRequired
            >
              <CheckableFields ariaLabelledBy='canEditWellLabel' isHorizontal>
                <CheckableField
                  id='canEditWellYes'
                  name='canEditWell'
                  describedBy='canEditWellLabel'
                  label='Yes'
                  isSkeleton={isLoading}
                  type='radio'
                  checked={!!fields.canEditWell}
                  onChange={() => setValue('canEditWell', true)}
                />
                <CheckableField
                  id='canEditWellNo'
                  name='canEditWell'
                  describedBy='canEditWellLabel'
                  label='No'
                  isSkeleton={isLoading}
                  type='radio'
                  checked={!fields.canEditWell}
                  onChange={() => setValue('canEditWell', false)}
                />
              </CheckableFields>
            </FormField>
            <Divider />
            <Heading level={2}>Primary contact user details</Heading>

            <FormField
              isSkeleton={isLoading}
              label='Primary contact email'
              htmlFor='email'
              validationMessages={validationErrors?.email}
              isRequired
            >
              <LookupSearch
                selectType='async-creatable'
                isSkeleton={isLoading}
                searchFunction={(value: string) =>
                  getLookupData('companyContactOptions', user?.token ?? '', user?.auth_method, {
                    user: value,
                    company: fields.company ?? '',
                    council: fields.companyCouncil ?? '',
                    isNewCompany,
                  })
                }
                fieldId='companyContactOptions'
                labelledBy='email'
                value={`${fields.companyContact ?? ''}`}
                setValue={(option: TypeaheadOption, isNew) => {
                  const { value, id, email, firstName, lastName, contactNumber } = option
                  setValue('companyContact', isNew ? value : id)
                  setValue('email', isNew ? value : email)
                  setIsNewUser(!!isNew)
                  if (!isNew) {
                    setValue('firstName', firstName)
                    setValue('lastName', lastName)
                    setValue('contactNumber', contactNumber)
                  }
                }}
              />
            </FormField>
            <FormField
              isSkeleton={isLoading}
              label='First name'
              htmlFor='firstName'
              validationMessages={validationErrors?.firstName}
              isRequired
            >
              <TextInput
                isSkeleton={isLoading}
                id='firstName'
                value={fields.firstName ?? ''}
                setValue={(value: string) => setValue('firstName', value)}
                isDisabled={!!fields.firstName && !isNewUser}
              />
            </FormField>
            <FormField
              isSkeleton={isLoading}
              label='Last name'
              htmlFor='lastName'
              validationMessages={validationErrors?.lastName}
              isRequired
            >
              <TextInput
                isSkeleton={isLoading}
                id='lastName'
                value={fields.lastName ?? ''}
                setValue={(value: string) => setValue('lastName', value)}
                isDisabled={!!fields.lastName && !isNewUser}
              />
            </FormField>
            <FormField isSkeleton={isLoading} label='Contact number' htmlFor='contactNumber'>
              <TextInput
                isSkeleton={isLoading}
                id='contactNumber'
                value={fields.contactNumber ?? ''}
                setValue={(value: string) => setValue('contactNumber', value)}
                isDisabled={!!fields.contactNumber && !isNewUser}
              />
            </FormField>
            <Divider />
            <Heading level={2}>Primary contact user permissions</Heading>
            <FormField
              isSkeleton={isLoading}
              label='Role'
              labelId='role'
              validationMessages={validationErrors?.role}
              isRequired
            >
              <LookupField
                isSkeleton={isLoading}
                fieldId='companyContactRole'
                fieldFilters={{ company_type: fields.companyType ?? '' }}
                labelledBy='role'
                value={fields.role ?? ''}
                setValue={({ value }) => setValue('role', value)}
              />
            </FormField>
          </>
        )}
      </RecordContent>
      <RecordFooter>
        <WellEditButtons
          actions={[
            {
              label: 'Save',
              buttonStyle: 'primary',
              onClick: () => {
                handleSave(async (id, refetch) => {
                  const postSaveUrl = organisationId === 'new' ? cancelRoute : `/admin/organisations/${id}/edit`
                  await refetch?.()
                  navigate(postSaveUrl, {
                    state: {
                      isProgrammaticTransition: true,
                    },
                  })
                })
              },
            },
            {
              label: 'Return to view',
              buttonStyle: 'text-action',
              onClick: () => navigate(cancelRoute),
            },
          ]}
        />
      </RecordFooter>
    </RecordContainer>
  )
}

export default function EditOrganisation() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <Organisation setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
